/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useContext, useEffect } from 'react';
import { CheckBox, Text, Types } from '@marriott/mi-ui-library';
import { StyledConsentCheckbox } from './ConsentCheckbox.styles';
import { useQuery, useMutation } from '@apollo/client';
import {
  phoenixPrearrivalGetCustomerPreferenceFeatures,
  phoenixPrearrivalUpdateCustomerPreferenceFeatures,
} from '../../graphql';
import { ConsentCheckboxProps } from './ConsentCheckbox.types';
import DOMPurify from 'isomorphic-dompurify';
import { PageContext } from '../../context';
import { generateApolloClientHeaders, handleRedirect } from '../../util/helpers';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { usePathname } from 'next/navigation';
import { updateDatalayer } from '../../util/dataLayer/datalayerUtil';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { isValidLuxuryBrand } from '../../util/constants';
const defaultConsentModel = [
  {
    consentDescription:
      'I consent to the collection and use of my preferences by the Marriott Group to deliver personalized services. Preferences can be updated any time by returning to this page.',
    consentKey: 'OPTED_OUT_PERSONALIZATION',
    consentTitle: '',
  },
];

const ConsentCheckbox: React.FC<ConsentCheckboxProps> = ({ checked, fragmentString, privacyText }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const htmlContent = fragmentString;
  const purifiedContent = DOMPurify.sanitize(htmlContent, {
    RETURN_DOM: true,
    ALLOWED_ATTR: ['href', 'target', 'class'],
  }) as HTMLElement;

  const pathName = usePathname();

  const handleNextPage = (newPart?: string) => {
    const currentPath = pathName;
    const newPath = currentPath.replace(/\/[^/]*$/, `/${newPart}`);
    handleRedirect(newPath); // WE NEED TO DO FULL RELOAD IN THANK YOU PAGE AFTER SIGN IN
    // router.push(newPath);
  };

  const purifiedContentText = purifiedContent.innerHTML;
  const [checkBoxState, setCheckBoxState] = useState(true);
  const { formData, setFormData } = useCreateSurveyStore();
  const [emeaConsent, setEmeaConsent] = useState<
    Array<{
      type: string;
      value: boolean;
    }>
  >([]);

  const [checkboxData, setCheckboxData] = useState<
    Array<{
      consentDescription: any;
      consentKey: any;
      consentTitle: any;
    }>
  >(defaultConsentModel);

  const [saveCustomerFeaturesPayload, setSavedCustomerFeaturesPayload] = useState([]);
  const [showPrivacyAfterDelay, setShowPrivacyAfterDelay] = useState(false); // flag to switch to privacy text after consent shown briefly
  const [isDataReady, setIsDataReady] = useState(false); //new loading flag for graphql
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);
  const [globalSessionData, setGlobalSessionData] = useState<any>(null);
  const pageContext = useContext(PageContext);

  const { IS_LOCAL_DEV } = process.env;

  // constants
  const EMEA_OPTED_OUT_PERSONALIZATION_KEY = 'OPTED_OUT_PERSONALIZATION';

  // initialize sessionData
  useEffect(() => {
    if (globalData !== undefined && globalData != null) {
      setGlobalSessionData(globalData);
    }
  }, [globalData]);
  const sessionData = globalSessionData?.sessionData?.cacheData?.data || pageContext?.sessionData?.cacheData?.data;

  // initialize consent model and checkbox state
  useEffect(() => {
    setCheckboxData(defaultConsentModel);

    const updatedEmeaConsent = defaultConsentModel.map(code => ({
      type: code.consentKey,
      value: checked,
    }));

    setEmeaConsent([...updatedEmeaConsent]);
  }, []);

  // Determine if we should fetch user preferences (only after user is signed in)
  // fetch trigger condition
  const [shouldFetch, setShouldFetch] = useState(false);
  useEffect(() => {
    setShouldFetch(!!sessionData?.consumerID && formData?.isUserEnableSignIn && !formData?.isRememberUser);
  }, [sessionData?.consumerID, formData?.isUserEnableSignIn, formData?.isRememberUser]);

  const { data, loading, error } = useQuery(phoenixPrearrivalGetCustomerPreferenceFeatures, {
    variables: {
      customerId: sessionData?.consumerID,
    },
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    skip: !shouldFetch,
  });

  useEffect(() => {
    if (loading) {
      setIsDataReady(false); // hide ui while loading
      return;
    }

    if (error || !data) {
      setIsDataReady(true);
      return;
    }

    // Process data only after query sucess
    const features =
      data?.customer?.featuresByGroup?.preferencesFeatures?.filter(
        (f: any) => f?.feature?.enumCode === EMEA_OPTED_OUT_PERSONALIZATION_KEY
      ) || [];

    setSavedCustomerFeaturesPayload(
      features.map((f: any) => ({
        code: f.feature.enumCode,
        value: f.value,
      }))
    );

    const updatedEmeaConsent = features.map((code: any) => ({
      type: code.feature.enumCode,
      value: code.value === 'N' ? true : false,
    }));

    // update form data with user's consent state
    setEmeaConsent([...updatedEmeaConsent]);
    setFormData({
      ...formData,
      isUserConsentChecked: updatedEmeaConsent[0]?.value,
    });

    // if user has consented, show consented checkbox for few seconds before showing privacy text
    if (updatedEmeaConsent[0]?.value) {
      setShowPrivacyAfterDelay(false);
      setTimeout(() => {
        setShowPrivacyAfterDelay(true);
      }, 2000); // 2 seconds delay
    }

    setIsDataReady(true); //data fully processed
  }, [data, loading, error]);

  const updateConsentsPayload = (consentsData: Array<{ type: string; value: boolean }>) => {
    const updatedFeatures = consentsData
      .filter(item => item.type === EMEA_OPTED_OUT_PERSONALIZATION_KEY)
      .map(item => ({
        code: item.type,
        value: item.value ? 'N' : 'Y',
      }));

    if (!updatedFeatures.length) {
      return { updatedFeatures: saveCustomerFeaturesPayload };
    }
    return { updatedFeatures };
  };

  const [updateCustomerConsents] = useMutation(phoenixPrearrivalUpdateCustomerPreferenceFeatures);

  // Function to save user consent to backend
  const saveConsent = () => {
    const { updatedFeatures } = updateConsentsPayload([
      {
        type: 'OPTED_OUT_PERSONALIZATION',
        value: formData?.consentCheckbox,
      },
    ]);
    const customerFeaturesInput = {
      customerFeatures: updatedFeatures,
      customerId: sessionData?.consumerID,
    };

    updateCustomerConsents({
      variables: {
        customerFeaturesInput,
      },
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
      onCompleted: (data: any) => {
        const features =
          data?.customer?.featuresByGroup?.preferencesFeatures?.filter(
            (f: any) => f?.feature?.enumCode === EMEA_OPTED_OUT_PERSONALIZATION_KEY
          ) || [];

        const updatedEmeaConsent = features.map((code: any) => ({
          type: code.feature.enumCode,
          value: code.value === 'N' ? true : false,
        }));

        // update global form data after save
        setFormData({
          ...formData,
          isUserConsentChecked: updatedEmeaConsent[0]?.value,
          isSubmitDataFlow: false,
          isReviewPageFlow: false,
          isSaveConsentCall: true,
        });

        // show consent checkbox briefly after user consents
        if (updatedEmeaConsent[0]?.value) {
          setShowPrivacyAfterDelay(false);
          setTimeout(() => {
            setShowPrivacyAfterDelay(true);
          }, 2000);
        }
      },
      onError: () => {},
    });

    updateDatalayer('form_step_name', 'Thank You');
    handleNextPage('thank-you.mi');
  };

  /**
   * Handle checkbox toggle event.
   * Toggles the value in emeaConsent and updates formData.
   */
  const changeConsentsHandler = (checkboxItem: { consentKey: string }) => {
    const updatedConsents = emeaConsent.map(item =>
      item.type === checkboxItem.consentKey ? { ...item, value: !item.value } : item
    );
    setEmeaConsent(updatedConsents);
    setFormData({
      ...formData,
      consentCheckbox: !formData.consentCheckbox,
    });
  };

  useEffect(() => {
    if (formData?.consentCheckbox) {
      setCheckBoxState(true);
    } else {
      setCheckBoxState(false);
    }
  }, [formData?.consentCheckbox]);

  // Auto-save consent when user logs in mid-flow
  useEffect(() => {
    if (
      formData?.isUserEnableSignIn &&
      !formData?.isSubmitDataFlow &&
      formData?.isReviewPageFlow &&
      formData?.consentCheckbox &&
      emeaConsent != undefined &&
      emeaConsent[0]?.value === false
    ) {
      setTimeout(() => {
        saveConsent();
      }, 1000);
    }
    if (
      formData?.isUserEnableSignIn &&
      formData?.isReviewPageFlow &&
      formData?.isSubmitDataFlow &&
      !formData?.isRememberUser
    ) {
      updateDatalayer('form_step_name', 'Thank You');
      handleNextPage('thank-you.mi');
    }
  }, [formData?.isUserEnableSignIn, formData?.isReviewPageFlow, formData?.isSubmitDataFlow]);

  /**
   * UI Rendering Logic:
   * - If user not signed in → show consent checkbox
   * - If signed in but not consented → show unchecked consent checkbox
   * - If signed in and consented → show consented checkbox for 2 seconds, then privacy text
   */
  const shouldShowConsent =
    ((!formData?.isUserEnableSignIn && !formData?.isUserConsentChecked) ||
      (formData?.isUserEnableSignIn && !formData?.isUserConsentChecked)) &&
    isValidLuxuryBrand(formData?.brandCode);

  useEffect(() => {
    if (shouldShowConsent) {
      setFormData({
        ...formData,
        consentCheckbox: checkBoxState,
      });
    }
  }, [checkBoxState]);

  const shouldShowPrivacy = formData?.isUserConsentChecked && showPrivacyAfterDelay;

  if (loading || !isDataReady) {
    return <></>;
  }

  return (
    <>
      {shouldShowConsent && !shouldShowPrivacy && !formData.isSignInFeatureDisable ? (
        <StyledConsentCheckbox>
          {checkboxData.map((item, index) => (
            <CheckBox
              key={index}
              className="t-font-s"
              checkboxId={`consent-checkbox-${index}`}
              checkboxName="consentFormCheckbox"
              checkboxLabel={item.consentTitle}
              checked={checkBoxState}
              onChange={() => changeConsentsHandler(item)}
            />
          ))}

          <Text
            element={Types.tags.div}
            fontSize={Types.size.medium}
            dangerouslySetInnerHTML={{
              __html: purifiedContentText,
            }}
            customClass="consent-privacytext Mb-2 pl-4"
          />
        </StyledConsentCheckbox>
      ) : (
        <Text
          element={Types.tags.div}
          fontSize={Types.size.medium}
          dangerouslySetInnerHTML={{
            __html: privacyText.replace(
              /Privacy Center/g,
              '<a href="https://www.marriott.com/about/privacy.mi" target="_blank">Privacy Center</a>'
            ),
          }}
          customClass="privacytext mb-2 "
        ></Text>
      )}
    </>
  );
};

export default ConsentCheckbox;
